import React from "react";
import { AlertTriangle, FileText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { fmtUSD } from "../../lib/format";
import { gaEvent } from "../../lib/ga";
import { track } from "../../lib/track";

let _sliderTimer;

const MoneyField = ({ label, testid, value, onChange }) => (
  <div>
    <label className="text-xs font-medium text-brand-stone" htmlFor={testid}>{label}</label>
    <div className="mt-1 flex items-center rounded-lg border border-brand-hairline bg-white focus-within:ring-1 focus-within:ring-brand-emerald">
      <span className="pl-3 text-brand-stone">$</span>
      <input id={testid} data-testid={testid} inputMode="numeric" value={value === 0 ? "" : Number(value).toLocaleString("en-US")}
        onChange={(e) => onChange(Number(e.target.value.replace(/[^0-9.]/g, "")) || 0)}
        className="w-full bg-transparent px-2 py-2.5 font-mono text-sm outline-none text-brand-ink" placeholder="0" />
    </div>
  </div>
);

const PctField = ({ label, testid, value, onChange }) => (
  <div>
    <label className="text-xs font-medium text-brand-stone" htmlFor={testid}>{label}</label>
    <div className="mt-1 flex items-center rounded-lg border border-brand-hairline bg-white focus-within:ring-1 focus-within:ring-brand-emerald">
      <input id={testid} data-testid={testid} inputMode="decimal" value={value}
        onChange={(e) => onChange(Number(e.target.value.replace(/[^0-9.]/g, "")) || 0)}
        className="w-full bg-transparent px-3 py-2.5 font-mono text-sm outline-none text-brand-ink" />
      <span className="pr-3 text-brand-stone">%</span>
    </div>
  </div>
);

export default function NetSheet({ property, inputs, setInputs, result, cities, onRequestReport }) {
  const set = (patch) => setInputs((p) => ({ ...p, ...patch }));
  const setCommission = (field) => (v) => { gaEvent("calculator_interaction", { field, value: v }); clearTimeout(_sliderTimer); _sliderTimer = setTimeout(() => track("slider_change", { field, value: v }), 500); set({ [field]: v }); };
  const v = property.valuation;
  const scenarios = [["Low", v.low], ["Midpoint", v.estimate], ["High", v.high]];
  const totals = result?.totals;
  const escrow = result?.escrow;

  return (
    <div id="step-5-seller-net-sheet" className="grid lg:grid-cols-5 gap-6 scroll-mt-24" data-testid="net-sheet">
      {/* Inputs */}
      <div className="lg:col-span-3 rounded-2xl bg-white border border-brand-hairline p-6 sm:p-8 space-y-6">
        <div>
          <span className="eyebrow">Step 5 · Seller Net Sheet</span>
          <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-brand-ink">Estimate your net proceeds</h2>
        </div>

        <div>
          <MoneyField label="Expected sale price" testid="net-sale-price" value={inputs.sale_price} onChange={(v) => set({ sale_price: v })} />
          <div className="mt-2 flex gap-2">
            {scenarios.map(([lbl, val]) => (
              <button key={lbl} onClick={() => set({ sale_price: val })} data-testid={`scenario-${lbl.toLowerCase()}`}
                className={`rounded-full px-3 py-1 text-xs border transition-colors ${inputs.sale_price === val ? "bg-brand-emerald text-brand-cream border-brand-emerald" : "border-brand-hairline text-brand-stone hover:border-brand-emerald"}`}>
                {lbl} · {fmtUSD(val)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-brand-stone" htmlFor="city-tax-jurisdiction-select">Property jurisdiction (city)</label>
            <Select value={inputs.city} onValueChange={(val) => set({ city: val })}>
              <SelectTrigger id="city-tax-jurisdiction-select" data-testid="city-tax-jurisdiction-select" className="mt-1 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {cities.map((c) => <SelectItem key={c} value={c} data-testid={`city-option-${c}`}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[0.65rem] text-brand-stone/60">Verify jurisdiction by parcel, not ZIP alone.</p>
          </div>
          <MoneyField label="Mortgage payoff" testid="mortgage-payoff-input" value={inputs.mortgage_payoff} onChange={(v) => set({ mortgage_payoff: v })} />
        </div>

        <div className="rounded-xl bg-brand-sand p-4 space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-stone">Commissions</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <PctField label="Equitas listing fee" testid="listing-fee-pct" value={inputs.listing_fee_pct} onChange={setCommission("listing_fee_pct")} />
            <div className="flex flex-col justify-end">
              <label className="flex items-center gap-2 text-xs text-brand-stone">
                <Switch checked={inputs.buyer_side_enabled} onCheckedChange={(v) => set({ buyer_side_enabled: v })} data-testid="buyer-side-switch" />
                Seller pays buyer-side compensation
              </label>
            </div>
          </div>
          {inputs.buyer_side_enabled && !inputs.dual_agency && (
            <PctField label="Buyer-side compensation" testid="buyer-side-pct" value={inputs.buyer_side_pct} onChange={setCommission("buyer_side_pct")} />
          )}
          {inputs.dual_agency && <p className="text-xs text-brand-emerald">Dual-agency scenario active: buyer-side fixed at 1% (Equitas both sides).</p>}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <MoneyField label="Title insurance" testid="title-insurance-input" value={inputs.title_insurance} onChange={(v) => set({ title_insurance: v })} />
          <MoneyField label="HOA transfer & doc fees" testid="hoa-input" value={inputs.hoa_fees} onChange={(v) => set({ hoa_fees: v })} />
          <MoneyField label="Repairs & seller credits" testid="repairs-input" value={inputs.repairs_credits} onChange={(v) => set({ repairs_credits: v })} />
          <MoneyField label="Staging reimbursement" testid="staging-input" value={inputs.staging} onChange={(v) => set({ staging: v })} />
          <MoneyField label="Other liens / expenses" testid="other-input" value={inputs.other_liens} onChange={(v) => set({ other_liens: v })} />
          <PctField label="Escrow — seller share" testid="escrow-seller-pct" value={inputs.escrow_seller_pct} onChange={(v) => set({ escrow_seller_pct: v })} />
        </div>

        {/* Transfer tax allocation editor */}
        {result?.transfer_taxes?.items?.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-stone mb-2">Transfer tax — seller allocation</p>
            <div className="space-y-2">
              {result.transfer_taxes.items.map((t) => (
                <div key={t.rule_id} className="flex items-center gap-3 text-sm">
                  <span className="flex-1 text-brand-stone flex items-center gap-1">
                    {t.name}{t.flagged_for_review && <AlertTriangle className="h-3.5 w-3.5 text-brand-gold" title="Flagged for review" />}
                  </span>
                  <input inputMode="numeric" data-testid={`tax-alloc-${t.rule_id}`}
                    value={(inputs.tax_allocations?.[t.rule_id] ?? t.seller_allocation_pct)}
                    onChange={(e) => set({ tax_allocations: { ...inputs.tax_allocations, [t.rule_id]: Number(e.target.value.replace(/[^0-9]/g, "")) || 0 } })}
                    className="w-16 rounded-lg border border-brand-hairline px-2 py-1.5 font-mono text-xs text-right outline-none focus:ring-1 focus:ring-brand-emerald" />
                  <span className="text-brand-stone text-xs w-4">%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky summary */}
      <div className="lg:col-span-2">
        <div className="lg:sticky lg:top-24 rounded-2xl bg-brand-dark text-brand-cream p-6 sm:p-8">
          <span className="eyebrow">Estimated Net Proceeds</span>
          <p className="mt-2 font-mono text-4xl font-bold text-brand-gold" data-testid="net-sheet-proceeds-amount">
            {totals ? fmtUSD(totals.estimated_net_proceeds) : "—"}
          </p>
          <p className="mt-1 text-xs text-brand-cream/50">Sale price − commissions − transfer taxes − payoff − other expenses.</p>

          <div className="mt-6 space-y-1.5 text-sm max-h-[320px] overflow-y-auto pr-1">
            {result?.line_items?.map((li, i) => (
              <div key={i} className="flex justify-between gap-3 py-1 border-b border-brand-cream/10">
                <span className="text-brand-cream/70 text-xs flex items-center gap-1">
                  {li.label}{li.flagged && <AlertTriangle className="h-3 w-3 text-brand-gold" />}
                </span>
                <span className="font-mono text-xs whitespace-nowrap">
                  {li.quote_required ? <span className="text-brand-gold">Quote req.</span>
                    : `${li.type === "debit" ? "−" : ""}${fmtUSD(li.amount)}`}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-4 flex justify-between border-t border-brand-cream/20 pt-3 text-sm">
            <span className="text-brand-cream/70">Total seller expenses</span>
            <span className="font-mono">{totals ? fmtUSD(totals.total_seller_expenses) : "—"}</span>
          </div>

          {escrow && (
            <p className="mt-4 text-[0.7rem] text-brand-cream/50 leading-relaxed">
              {escrow.label} {escrow.disclaimer}
            </p>
          )}
          <a href="https://www.chicagotitleratecalculator.com/" target="_blank" rel="noopener noreferrer"
            data-testid="escrow-quote-link" className="mt-2 inline-block text-[0.7rem] underline text-brand-cream/70 hover:text-brand-gold">
            Get an official Chicago Title escrow quote →
          </a>

          <button onClick={onRequestReport} data-testid="open-pdf-modal-button"
            className="mt-6 w-full flex items-center justify-center gap-2 rounded-xl bg-brand-gold px-6 py-3.5 font-semibold text-brand-dark hover:brightness-95 transition-all">
            <FileText className="h-4 w-4" /> Download Full PDF Report
          </button>
          <p className="mt-2 text-center text-[0.7rem] text-brand-cream/40">No registration needed to view — only to download.</p>
        </div>
      </div>
    </div>
  );
}
